import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { getUserSubject } from '../../../../../store/User';
import {
  InvalidLink,
  fetchInvalidLinks,
  getCruxInvalidLinksLoadingSubject,
  getCruxInvalidLinksSubject
} from '../../../../../store/Link';
import { DefaultInvalidLinkGridColumnDefs } from '../../ColumnDefs/InvalidLinkGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns, getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { getDefaultPreferences, GridPreferences } from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { GridCustomColDef } from '../../../../types';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { PageLayout } from '../../../../PageLayout';

export const InvalidLinkTab = () => {
  const [invalidLinksLoading, setInvalidLinksLoading] = useState<boolean>(false);
  const [invalidLinks, setInvalidLinks] = useState<InvalidLink[]>([]);
  const [invalidLinkColumns, setInvalidLinkColumns] = useState<GridCustomColDef[]>(DefaultInvalidLinkGridColumnDefs);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['invalidLinkGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  const navigate = useNavigate();

  useEffect(() => {
    const loadingSubscription = getCruxInvalidLinksLoadingSubject().subscribe((loading) =>
      setInvalidLinksLoading(loading)
    );
    const invalidLinksSub = getCruxInvalidLinksSubject().subscribe((links) => setInvalidLinks(links));
    const userSub = getUserSubject().subscribe((usr) => {
      if (usr?.preferences?.content?.invalidLinkGrid) {
        setGridSettings(usr.preferences.content.invalidLinkGrid);
        const state = getInitialState(
          usr.preferences.content.invalidLinkGrid,
          DefaultInvalidLinkGridColumnDefs,
          'invalidLinkGrid'
        );
        setInitialState(state);
      }
    });

    fetchInvalidLinks();

    return () => {
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (invalidLinksSub) invalidLinksSub.unsubscribe();
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!invalidLinksLoading && invalidLinks.length > 0) {
      setInvalidLinkColumns(getGridColumns(invalidLinks, DefaultInvalidLinkGridColumnDefs, 'invalidLinkGrid'));
    }
  }, [invalidLinksLoading, invalidLinks.length]);

  const handleMerge = () => {
    navigate('/bulk-merge');
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleMerge}
      data-cy="merge-links-button"
      data-testid="merge-links-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        Merge Links
      </TextSpan>
    </button>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="invalid-link-page"
      dataCy="invalid-link-page-card"
      pageTitle="">
      <PageCard
        dataTestId="invalid-link-management"
        dataCy="invalid-link-management"
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title="Invalid Links"
        cardActions={cardActions()}>
        <>
          {!initialState ||
            (invalidLinksLoading && (
              <div className="flex h-[45rem] m-auto">
                <ComponentSpinner label="Loading data..." />
              </div>
            ))}
          {initialState && !invalidLinksLoading && (
            <UpstackDataGrid
              title="Invalid Links"
              rows={invalidLinks}
              columns={invalidLinkColumns}
              page="invalidLinkGrid"
              gridSettings={gridSettings}
              showSearch={true}
              initialState={initialState}
              apiFilter={false}
              sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            />
          )}
        </>
      </PageCard>
    </PageLayout>
  );
};
